// Géométrie en pixels CSS, sans DOM ni Chart.js. Les dimensions du texte et les points
// de contrôle du tracé viennent du rendu : la même fonction sert à l'écran et aux exports.
export interface PointCourbe {
  x: number; y: number; valeur: number
  largeur: number; hauteur: number
  rayon?: number; etiqueter?: boolean
  cp1x?: number; cp1y?: number; cp2x?: number; cp2y?: number
}
export interface SerieCourbe { points: (PointCourbe | null)[]; epaisseur?: number }
interface Cadre { left: number; right: number; top: number; bottom: number }
interface Boite { x1: number; y1: number; x2: number; y2: number }
export interface PlacementCourbe extends Boite { serie: number; point: number }
interface Position { x: number; y: number }
interface Segment { a: Position; b: Position; marge: number }

const milieu = (a: Position, b: Position): Position => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })
function distance(p: Position, a: Position, b: Position): number {
  const dx = b.x - a.x, dy = b.y - a.y
  const t = dx || dy ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy))) : 0
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy)
}

function segmentsCourbes(series: SerieCourbe[]): Segment[] {
  const segments: Segment[] = []
  // Une Bézier reste dans l'enveloppe de ses points de contrôle. La subdivision de
  // De Casteljau borne donc l'écart à la corde ; on ajoute cet écart à la marge du trait.
  const decouper = (a: Position, c: Position, d: Position, b: Position, marge: number, niveau = 0) => {
    const ecart = Math.max(distance(c, a, b), distance(d, a, b))
    if (ecart <= 0.25 || niveau >= 12) { segments.push({ a, b, marge: marge + ecart }); return }
    const ac = milieu(a, c), cd = milieu(c, d), db = milieu(d, b)
    const gauche = milieu(ac, cd), droite = milieu(cd, db), centre = milieu(gauche, droite)
    decouper(a, ac, gauche, centre, marge, niveau + 1)
    decouper(centre, droite, db, b, marge, niveau + 1)
  }
  for (const serie of series) for (let i = 1; i < serie.points.length; i++) {
    const a = serie.points[i - 1], b = serie.points[i]
    if (!a || !b) continue // une lacune coupe réellement le tracé
    const marge = (serie.epaisseur ?? 2.5) / 2 + 2
    if ([a.cp2x, a.cp2y, b.cp1x, b.cp1y].every(v => v !== undefined && Number.isFinite(v))) {
      decouper(a, { x: a.cp2x!, y: a.cp2y! }, { x: b.cp1x!, y: b.cp1y! }, b, marge)
    } else segments.push({ a, b, marge })
  }
  return segments
}

/** Intersection d'un segment et du rectangle élargi par la demi-épaisseur du trait. */
function traverse(s: Segment, b: Boite): boolean {
  let debut = 0, fin = 1
  for (const [origine, delta, min, max] of [
    [s.a.x, s.b.x - s.a.x, b.x1 - s.marge, b.x2 + s.marge],
    [s.a.y, s.b.y - s.a.y, b.y1 - s.marge, b.y2 + s.marge],
  ]) {
    if (delta === 0) { if (origine < min || origine > max) return false }
    else {
      const t1 = (min - origine) / delta, t2 = (max - origine) / delta
      debut = Math.max(debut, Math.min(t1, t2)); fin = Math.min(fin, Math.max(t1, t2))
      if (debut > fin) return false
    }
  }
  return true
}

/** La grille ne retient que les obstacles voisins du rectangle. Les intersections
 *  précises restent les mêmes, y compris la marge des subdivisions Bézier. */
function indexerObstacles(series: SerieCourbe[], cadre: Cadre): (b: Boite) => boolean {
  interface Obstacle extends Boite { segment?: Segment; point?: PointCourbe }
  const cellules = new Map<string, Obstacle[]>()
  const visiter = (b: Boite, lire: (cle: string) => boolean): boolean => {
    const x1 = Math.floor(Math.max(b.x1, cadre.left) / 32), x2 = Math.floor(Math.min(b.x2, cadre.right) / 32)
    const y1 = Math.floor(Math.max(b.y1, cadre.top) / 32), y2 = Math.floor(Math.min(b.y2, cadre.bottom) / 32)
    for (let x = x1; x <= x2; x++) for (let y = y1; y <= y2; y++) {
      if (lire(`${x},${y}`)) return true
    }
    return false
  }
  const ajouter = (o: Obstacle) => visiter(o, cle => {
    const cellule = cellules.get(cle)
    if (cellule) cellule.push(o)
    else cellules.set(cle, [o])
    return false
  })
  for (const s of segmentsCourbes(series)) ajouter({ segment: s,
    x1: Math.min(s.a.x, s.b.x) - s.marge, x2: Math.max(s.a.x, s.b.x) + s.marge,
    y1: Math.min(s.a.y, s.b.y) - s.marge, y2: Math.max(s.a.y, s.b.y) + s.marge })
  for (const serie of series) for (const p of serie.points) {
    if (!p) continue
    const marge = (p.rayon ?? 3) + 2
    ajouter({ point: p, x1: p.x - marge, x2: p.x + marge, y1: p.y - marge, y2: p.y + marge })
  }
  return b => {
    const vus = new Set<Obstacle>()
    return visiter(b, cle => (cellules.get(cle) ?? []).some(o => {
      if (vus.has(o)) return false
      vus.add(o)
      if (b.x2 < o.x1 || b.x1 > o.x2 || b.y2 < o.y1 || b.y1 > o.y2) return false
      if (o.segment) return traverse(o.segment, b)
      const q = o.point!
      return Math.hypot(q.x - Math.max(b.x1, Math.min(q.x, b.x2)),
        q.y - Math.max(b.y1, Math.min(q.y, b.y2))) <= (q.rayon ?? 3) + 2
    }))
  }
}

/** Réserve d'abord dernière valeur, maximum, minimum ; à rang égal la principale
 *  (série 0) précède les comparées. Aucun chiffre n'est forcé sur un obstacle : si le
 *  cadre ne peut physiquement pas le contenir, il reste dans l'infobulle et le tableau. */
export function placerEtiquettesCourbes(series: SerieCourbe[], cadre: Cadre): PlacementCourbe[] {
  const heurte = indexerObstacles(series, cadre)
  const reperes: { serie: number; point: number; rang: number; p: PointCourbe }[] = []
  series.forEach((s, serie) => {
    const indices = s.points.flatMap((p, i) => p && p.etiqueter !== false ? [i] : [])
    if (!indices.length) return
    const dernier = indices[indices.length - 1]
    const max = indices.reduce((a, b) => s.points[a]!.valeur > s.points[b]!.valeur ? a : b)
    const min = indices.reduce((a, b) => s.points[a]!.valeur < s.points[b]!.valeur ? a : b)
    for (const point of indices) reperes.push({ serie, point, p: s.points[point]!,
      rang: point === dernier ? 0 : point === max ? 1 : point === min ? 2 : 3 })
  })
  reperes.sort((a, b) => a.rang - b.rang || a.serie - b.serie || a.point - b.point)
  const poses: PlacementCourbe[] = []
  for (const { p, serie, point, rang } of reperes) {
    if (p.largeur > cadre.right - cadre.left || p.hauteur > cadre.bottom - cadre.top) continue
    const precedent = series[serie].points[point - 1], suivant = series[serie].points[point + 1]
    const pic = precedent && suivant && p.y < precedent.y && p.y < suivant.y
    const creux = precedent && suivant && p.y > precedent.y && p.y > suivant.y
    const dessous = !pic && (creux || (precedent && precedent.y < p.y))
    const sens = dessous ? [1, -1] : [-1, 1]
    const demi = p.hauteur / 2
    const pas = p.hauteur + 4
    const depart = demi + (p.rayon ?? 3) + 3
    const limite = rang < 3 ? cadre.bottom - cadre.top : depart + pas
    const ecarts: number[] = []
    for (let ecart = depart; ecart <= limite; ecart += pas) ecarts.push(ecart)
    // En dernier recours, les repères prioritaires cherchent aussi à hauteur du
    // point et entre les rangées usuelles : une place étroite ne doit pas être sautée.
    const rangsUsuels = ecarts.length
    if (rang < 3) for (let ecart = 0; ecart <= limite; ecart += 2) ecarts.push(ecart)
    const gauches = [p.x - p.largeur / 2, p.x + 7, p.x - 7 - p.largeur]
    const intermediaires = [...gauches]
    if (rang < 3) for (let retrait = 4; retrait <= p.largeur; retrait += 4) {
      intermediaires.push(p.x + 7 + retrait, p.x - 7 - p.largeur - retrait)
    }
    let trouve = false
    for (const [rangEcart, ecart] of ecarts.entries()) {
      // Centrée en premier, puis juste à gauche/droite : dans une pente forte le
      // nombre doit pouvoir sortir du couloir occupé par les deux segments voisins.
      for (const direction of sens) {
        for (const gauche of rangEcart < rangsUsuels ? gauches : intermediaires) {
          const x1 = Math.max(cadre.left, Math.min(gauche, cadre.right - p.largeur))
          const y = p.y + direction * ecart
          const b = { x1, x2: x1 + p.largeur, y1: y - demi, y2: y + demi }
          if (b.y1 < cadre.top || b.y2 > cadre.bottom) continue
          if (poses.some(o => b.x1 < o.x2 + 3 && b.x2 + 3 > o.x1 && b.y1 < o.y2 + 2 && b.y2 + 2 > o.y1)) continue
          if (heurte(b)) continue
          poses.push({ ...b, serie, point }); trouve = true
          break
        }
        if (trouve) break
      }
      if (trouve) break
    }
  }
  return poses
}
